import { Button, DialogContent, DialogFooter, DialogHeader, DialogRoot, DialogTitle } from '@ibsys/kit';
import { FileText, X } from 'lucide-react';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { Dropzone } from '@/shared/ui/Dropzone';
import { IconButton } from '@/shared/ui/IconButton';

type FileUploadDialogPresenterProps = {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  uploadedFiles: File[];
  errors: string[];
  acceptedFileTypes: string[];
  maxFileSize: number;
  maxFiles: number;
  onFilesChange: (files: File[]) => void;
  onErrorsChange: (errors: string[]) => void;
  onRemoveFile: (index: number) => void;
  onUpload: () => void;
  onClose: () => void;
  isLoading?: boolean;
};

export const FileUploadDialogPresenter: FC<FileUploadDialogPresenterProps> = ({
  isOpen,
  onOpenChange,
  uploadedFiles,
  errors,
  acceptedFileTypes,
  maxFileSize,
  maxFiles,
  onFilesChange,
  onErrorsChange,
  onRemoveFile,
  onUpload,
  onClose,
  isLoading = false,
}) => {
  const { t } = useTranslation(['records']);

  return (
    <DialogRoot open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl w-[92vw] h-[80vh] flex flex-col overflow-hidden p-0">
        <DialogHeader className="border-b border-border p-6">
          <DialogTitle className="font-semibold w-max">{t('records:fileUpload.title')}</DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-auto p-2 md:p-8">
          {/* Upload Area */}
          <div className="mb-6">
            <Dropzone
              acceptedFileTypes={acceptedFileTypes}
              maxFileSize={maxFileSize}
              maxFiles={maxFiles}
              currentFilesCount={uploadedFiles.length}
              onFilesChange={onFilesChange}
              onErrorsChange={onErrorsChange}
              disabled={isLoading}
            />
          </div>

          {/* Error Messages */}
          {errors.length > 0 && (
            <div className="mb-6 rounded-lg border border-destructive/50 bg-destructive/10 p-4">
              <h4 className="mb-2 font-medium text-destructive">{t('records:fileUpload.uploadErrors')}</h4>
              <ul className="space-y-1 text-sm text-destructive/90">
                {errors.map((error, index) => (
                  <li key={index}>{`• ${error}`}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Uploaded Files */}
          {uploadedFiles.length > 0 && (
            <div>
              <h4 className="mb-3 font-semibold text-foreground">
                {`${t('records:fileUpload.uploadedFiles')} (${uploadedFiles.length})`}
              </h4>
              <div className="space-y-2">
                {uploadedFiles.map((file, index) => (
                  <div
                    key={file.name}
                    className="group flex items-center justify-between rounded-lg border border-border bg-muted/30 p-3 transition-colors hover:bg-muted"
                  >
                    <div className="flex items-center gap-3">
                      <div className="flex h-8 w-8 items-center justify-center rounded bg-primary/10">
                        <FileText className="h-4 w-4 text-primary" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground">{file.name}</p>
                        <p className="text-xs text-muted-foreground">{`${(file.size / (1024 * 1024)).toFixed(2)} MB`}</p>
                      </div>
                    </div>
                    <IconButton
                      size="sm"
                      onClick={() => onRemoveFile(index)}
                      disabled={isLoading}
                      className="opacity-0 transition-opacity group-hover:opacity-100 hover:bg-destructive/10 hover:text-destructive"
                      label={t('records:fileUpload.removeFile', { fileName: file.name })}
                      icon={<X className="h-4 w-4" />}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <DialogFooter className="border-t border-border bg-muted/30 p-6">
          <div className="flex flex-col w-full gap-1 justify-between">
            <p className="text-sm text-muted-foreground">
              {uploadedFiles.length > 0
                ? t('records:fileUpload.readyToUpload', { count: uploadedFiles.length })
                : t('records:fileUpload.selectFiles')}
            </p>
            <div className="flex gap-3 ml-auto">
              <Button variant="outline" onClick={onClose} disabled={isLoading}>
                {t('records:fileUpload.cancel')}
              </Button>
              <Button onClick={onUpload} disabled={uploadedFiles.length === 0 || isLoading} isLoading={isLoading}>
                {t('records:fileUpload.upload')} {uploadedFiles.length > 0 && `(${uploadedFiles.length})`}
              </Button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  );
};
